import React from "react";
import styled from "@emotion/styled";

import FrameworkStage from "components/framework/FrameworkStage";
import { useSubscription } from "lib/hooks/subscription";
import { usePaywall } from "lib/providers/Paywall";
import { sysBackgroundAlternative, sysTextSecondary } from "lib/themes/colors";
import { FrameworkStages, OpportunityEntityType } from "lib/types/graphQLEnums";
import { toHumanRelativeDateFromNow } from "lib/utils/relative_time";
import { OpportunityDetails } from "../../../lib/hooks/api/opportunities/useOpportunity";
import { Flex, Text } from "../../../styles/utility-components";
import { opportunitySavedCardContainer } from "./shared/styles";
import EntityCardContent from "./EntityCardContent";
import FindEntity from "./FindEntity";
import PaidTag from "./PaidTag";
import Tag from "./Tag";

type Props = {
  frameworks: OpportunityDetails["frameworks"];
  entityUrl: string;
  opportunityId: string;
};

export default function OpportunityFrameworks({ frameworks, entityUrl, opportunityId }: Props) {
  const handleViewFramework = (id: string) => () => {
    window.location.href = `/frameworks/${id}`;
  };

  return (
    <Flex column gap={16}>
      {frameworks.map((framework) => (
        <FrameworkCard
          key={framework.id}
          framework={framework}
          onView={handleViewFramework(framework.id)}
          opportunityId={opportunityId}
        />
      ))}
      <FindEntity entity="framework" url={entityUrl} isEmptyState={!frameworks.length} />
    </Flex>
  );
}

function FrameworkCard({
  framework,
  onView,
  opportunityId,
}: {
  framework: OpportunityDetails["frameworks"][0];
  onView: () => void;
  opportunityId: string;
}) {
  const subscription = useSubscription();
  const hasAccess = subscription.hasDataTypes("FRAMEWORKS");

  const { openPaywallModal } = usePaywall();

  const expiryDateInRelativeFormat = getExpiryDateInRelativeFormat(framework);

  return (
    <Container
      onClick={() => {
        if (!hasAccess) {
          openPaywallModal("FRAMEWORKS");
          return;
        }
        onView();
      }}
    >
      <EntityCardContent
        entity="framework"
        entityId={framework.id}
        entityType={OpportunityEntityType.Framework}
        entityName={framework.title}
        opportunityId={opportunityId}
      >
        <Flex column gap={16} width="100%">
          <FrameworkStage stage={framework.stage} />
          <Text fontWeight="600">{framework.title || "Untitled Framework"}</Text>
          <Flex gap={8} flexWrap="wrap">
            {expiryDateInRelativeFormat && (
              <Tag
                backgroundColor={sysBackgroundAlternative}
                fontColor={sysTextSecondary}
                title={expiryDateInRelativeFormat}
              />
            )}
            {!hasAccess && <PaidTag />}
          </Flex>
        </Flex>
      </EntityCardContent>
    </Container>
  );
}

function getExpiryDateInRelativeFormat(framework: OpportunityDetails["frameworks"][0]) {
  if (
    [FrameworkStages.Awarded, FrameworkStages.Live, FrameworkStages.Expired].includes(
      framework.stage,
    ) &&
    framework.contractPeriod?.endDate
  ) {
    const humanReadableDate = toHumanRelativeDateFromNow(framework.contractPeriod.endDate);
    return `${humanReadableDate.includes("ago") ? "Expired" : "Expiring"} ${humanReadableDate}`;
  } else if (framework.stage === FrameworkStages.Tendering && framework.tenderPeriod?.endDate) {
    const humanReadableDate = toHumanRelativeDateFromNow(framework.tenderPeriod.endDate);
    return `${humanReadableDate.includes("ago") ? "Closed" : "Closing"} ${humanReadableDate}`;
  }
  return null;
}

const Container = styled(Flex)(({ theme }) => ({
  ...opportunitySavedCardContainer(theme),
}));
